import { DragEndEvent } from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import { getCoreRowModel, getExpandedRowModel, useReactTable } from "@tanstack/react-table";
import { isEmpty } from "ramda";
import { clsx } from "@/utils";
import { NoSearchResults } from "../EmptyStates/NoSearchResults";
import { DataTableProps } from "./types";
import { DefaultTable } from "./variants/DefaultTable";
import { SortableTable } from "./variants/SortableTable";

export const tableClass = "rounded-lg w-full divide-y divide-neutral-mid-gray divide-opacity-100";

export const DataTable = <T extends object>({
  data,
  columns,
  onReorder,
  isRowDraggable,
  sorting,
  setSorting,
  canExpand,
  renderSubComponent,
  getRowProps,
  getIsRowExpanded,
  emptyState,
  className,
  maxHeight,
  ...props
}: DataTableProps<T>) => {
  // `-top-3` (paired with the outer box's `pt-3`) gives breathing room above the
  // header at rest that tucks away on scroll, pinning the header flush.
  const stickyHeaderClassName = maxHeight ? "sticky -top-3 z-20" : undefined;
  const table = useReactTable({
    data,
    columns,
    ...(sorting && {
      state: {
        sorting,
      },
      onSortingChange: setSorting,
      manualSorting: true,
    }),
    ...(canExpand && {
      getRowCanExpand: () => true,
      getExpandedRowModel: getExpandedRowModel(),
      getIsRowExpanded: getIsRowExpanded,
    }),
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: {
      minSize: 0,
      size: Number.MAX_SAFE_INTEGER,
      maxSize: Number.MAX_SAFE_INTEGER,
    },
  });

  const renderTable = () => {
    const restProps = { renderSubComponent, getRowProps };

    switch (true) {
      case isEmpty(data):
        return (
          <DefaultTable table={table} stickyHeaderClassName={stickyHeaderClassName}>
            <tr className="hover:!bg-white [&_section]:w-full">
              <td colSpan={columns.length}>{emptyState ? emptyState : <NoSearchResults />}</td>
            </tr>
          </DefaultTable>
        );

      case !!onReorder: {
        const onDragEnd = (e: DragEndEvent) => {
          const { active, over } = e;
          const rows = table.getRowModel().rows;

          if (active.id !== over?.id) {
            const activeIndex = rows.findIndex((i) => i.id === active.id);
            const overIndex = rows.findIndex((i) => i.id === over?.id);
            onReorder!(arrayMove(data, activeIndex, overIndex));
          }
        };
        return (
          <SortableTable
            table={table}
            onDragEnd={onDragEnd}
            isRowDraggable={isRowDraggable}
            stickyHeaderClassName={stickyHeaderClassName}
            {...restProps}
          />
        );
      }

      default:
        return <DefaultTable table={table} stickyHeaderClassName={stickyHeaderClassName} {...restProps} />;
    }
  };

  const cardClass =
    "relative rounded-lg ring-1 ring-neutral-mid-gray ring-opacity-100 [&_tbody_tr:hover]:bg-neutral-gray [&_#icon]:[&_tbody_tr:hover]:bg-neutral-mid-gray";

  // Default: the bordered card is the wrapper (no internal scroll).
  if (!maxHeight) {
    return (
      <div className={clsx(cardClass, className)} {...props}>
        {renderTable()}
      </div>
    );
  }

  // Sticky-header: a borderless outer box scrolls; the bordered card scrolls
  // inside it while the header stays pinned — so it's bordered and sticky.
  return (
    <div className={clsx("overflow-auto px-1 pb-1 pt-3", maxHeight, className)} {...props}>
      <div className={cardClass}>{renderTable()}</div>
    </div>
  );
};
